export const FINANCE_BRIEFING = {
  greeting: {
    date: 'Monday, Jan 27',
    meetings: 2,
    tasks: 4,
    financeAlerts: 1,
    lucMessage: "Hey Neel, heads up — Finn flagged that the Q1 revenue forecast was revised down to $342K after two enterprise deals slipped. Cashflow is still healthy but the runway projection needs updating. A couple of approvals waiting on you."
  },
  financials: [
    {
      id: 'f1',
      title: 'Q1 revenue forecast revised to $342K',
      agent: 'finn',
      category: 'forecast',
      alert: true,
      description: 'Two enterprise deals (TechCorp $52K, DataFlow $38K) slipped to Q2. Sally adjusted the forecast down from $380K.',
      expanded: {
        summary: 'Finn recalculated Q1 projections after Sally updated the pipeline. The $90K shortfall is partially offset by stronger SMB performance (+$22K). Net gap is $48K vs original plan.',
        details: [
          { label: 'Original Forecast', value: '$380,000' },
          { label: 'Revised Forecast', value: '$342,000 (-10%)' },
          { label: 'Slipped Deals', value: '2 ($90K combined)' },
          { label: 'SMB Offset', value: '+$22K above target' },
          { label: 'Net Gap', value: '$48K vs plan' },
          { label: 'Cashflow Impact', value: 'Manageable — 8.2 months runway' },
        ],
        thinking: 'Finn cross-referenced Sally\'s updated pipeline with the operating budget. The shortfall impacts Q1 EBITDA margin by 2.3 points but doesn\'t trigger any covenant breaches or require spending cuts. Recommends adjusting the board deck and monitoring the 3 late-stage SMB deals that could close the gap.',
        proposedActions: [
          { label: 'Update Q1 board forecast deck', agent: 'Finn' },
          { label: 'Adjust monthly cashflow model', agent: 'Finn' },
          { label: 'Flag gap to CEO with mitigation plan', agent: 'Luc' },
        ]
      }
    },
    {
      id: 'f2',
      title: 'Marketing budget reallocation request',
      agent: 'finn',
      category: 'budget',
      alert: false,
      description: 'Marsi requests $800 reallocation from underperforming Meta ad set to top-performing LinkedIn creative.',
      expanded: {
        summary: 'The Meta Brand Awareness campaign has an underperforming ad set (CTR 1.1% vs 2.8% target). Marsi wants to shift $800 to the LinkedIn creative which is outperforming at 3.4% CTR.',
        details: [
          { label: 'Amount', value: '$800 reallocation' },
          { label: 'From', value: 'Meta Ad Set B (CTR: 1.1%)' },
          { label: 'To', value: 'LinkedIn Creative A (CTR: 3.4%)' },
          { label: 'Budget Impact', value: 'Net zero — within existing allocation' },
          { label: 'Expected Improvement', value: '+8-12 additional leads' },
        ],
        thinking: 'Finn verified the reallocation is within the approved marketing budget. No additional spend required. The ROI on the LinkedIn creative is 3.1x the Meta ad set based on 5-day data. Low-risk optimization.'
      }
    },
    {
      id: 'f3',
      title: 'Senior Developer hire — budget impact analysis',
      agent: 'finn',
      category: 'headcount',
      alert: false,
      description: 'Tali\'s hiring requisition adds $12.5K/month to operating costs. Finn has assessed the runway impact.',
      expanded: {
        summary: 'The Senior Developer hire at $135K-$155K salary adds $12.5K/month to OpEx. Finn confirmed there is budget headroom and the hire doesn\'t impact runway below the 6-month safety threshold.',
        details: [
          { label: 'Monthly Cost', value: '$12,500 (fully loaded)' },
          { label: 'Annual Impact', value: '$150,000' },
          { label: 'Current Monthly Burn', value: '$185,000' },
          { label: 'Revised Monthly Burn', value: '$197,500' },
          { label: 'Current Runway', value: '8.2 months' },
          { label: 'Post-Hire Runway', value: '7.7 months' },
        ],
        thinking: 'Finn modeled the hire against 3 scenarios (low, mid, high revenue). Even in the low-revenue scenario, runway stays above 6 months. The productivity gains from reducing engineering overload should offset the cost within 2 sprints based on historical data.'
      }
    }
  ],
  tasks: {
    today: [
      {
        id: 'ft1',
        title: 'Approve marketing budget reallocation',
        agent: 'finn',
        urgency: 'due-today',
        description: 'Marsi\'s $800 reallocation from Meta to LinkedIn. Finn recommends approval — net zero budget impact.',
        expanded: {
          summary: 'Straightforward budget reallocation within approved marketing spend. Finn has validated the numbers and sees no risk.',
          artifacts: [
            { name: 'Budget Reallocation Form', type: 'pdf' },
            { name: 'Campaign ROI Comparison', type: 'spreadsheet' },
          ],
          thinking: 'Finn reviewed the request against the Q1 marketing budget. The reallocation is within Marsi\'s discretionary authority, but she escalated for transparency given the campaign is only 5 days old.'
        }
      },
      {
        id: 'ft2',
        title: 'Review updated cashflow projection',
        agent: 'finn',
        urgency: 'due-today',
        description: 'Finn adjusted the 90-day cashflow model after the Q1 forecast revision. Needs your sign-off before sharing with Arjun.',
        expanded: {
          summary: 'The revised cashflow model reflects the $48K revenue gap and the proposed Senior Developer hire. All scenarios maintain runway above 6 months.',
          details: [
            { label: 'Cash on Hand', value: '$1.52M' },
            { label: 'Monthly Burn (current)', value: '$185,000' },
            { label: 'Monthly Burn (projected)', value: '$197,500' },
            { label: 'Runway (current)', value: '8.2 months' },
            { label: 'Runway (projected)', value: '7.7 months' },
          ],
          artifacts: [
            { name: '90-Day Cashflow Model', type: 'spreadsheet' },
            { name: 'Scenario Analysis', type: 'pdf' },
          ]
        }
      },
      {
        id: 'ft3',
        title: 'Sign off on contractor payment batch',
        agent: 'finn',
        description: 'Monthly contractor payments ready for processing. 6 contractors, total $28,400.',
        expanded: {
          summary: 'Finn has verified all contractor invoices against approved SOWs. All payments are within budget.',
          details: [
            { label: 'Contractors', value: '6' },
            { label: 'Total Amount', value: '$28,400' },
            { label: 'Budget Status', value: 'Within Q1 allocation' },
            { label: 'Due Date', value: 'Jan 31' },
          ],
          artifacts: [
            { name: 'Payment Summary', type: 'spreadsheet' },
            { name: 'Invoice Batch', type: 'pdf' },
          ]
        }
      }
    ],
    ifTime: [
      { id: 'ft4', title: 'Review Q2 budget draft', agent: 'finn', description: 'Finn started the Q2 operating budget. Early draft ready for directional feedback.' },
      { id: 'ft5', title: 'Vendor contract renewal tracker', agent: 'finn', description: 'Finn flagged 3 vendor contracts expiring in Q1. May be opportunities to renegotiate.' }
    ]
  },
  financeMetrics: {
    snapshot: [
      { label: 'Cash on Hand', value: '$1.52M' },
      { label: 'Monthly Burn', value: '$185K', trend: '+5%' },
      { label: 'Runway', value: '8.2 months', trend: '-0.4' },
      { label: 'Q1 Revenue (proj)', value: '$342K', trend: '-10%', alert: true },
      { label: 'Gross Margin', value: '72%', trend: '+1%' },
    ],
    alert: {
      id: 'fa1',
      title: 'Q1 revenue gap — $48K below plan',
      agents: ['finn', 'sally'],
      severity: 'high',
      description: 'Two enterprise deals slipped to Q2, creating a $48K gap after SMB offset. Runway impact is minimal but EBITDA margin dips 2.3 points.',
      expanded: {
        details: [
          { label: 'Revenue Gap', value: '$48K vs plan' },
          { label: 'Slipped Deals', value: 'TechCorp ($52K) + DataFlow ($38K)' },
          { label: 'SMB Offset', value: '+$22K above target' },
          { label: 'EBITDA Impact', value: '-2.3 margin points' },
          { label: 'Runway Impact', value: 'Minimal (8.2 → 8.0 months)' },
        ],
        thinking: 'Finn and Sally assessed the gap jointly. The shortfall is timing-driven (deals delayed, not lost). Three late-stage SMB deals ($34K combined) could close the gap if accelerated. No spending cuts recommended at this stage.',
        proposedActions: [
          { label: 'Monitor 3 late-stage SMB deals weekly', agent: 'Finn' },
          { label: 'Prepare board talking points on Q1 variance', agent: 'Finn' },
          { label: 'Defer non-critical Q1 spend by 2 weeks', agent: 'Finn' },
        ]
      }
    }
  },
  meetings: [
    { id: 'fm1', title: 'Finance weekly review', time: '10:00 AM', duration: '30 min', with: 'Finance Team', type: 'internal', agenda: ['Q1 forecast revision walkthrough', 'Cashflow model update', 'Contractor payment batch sign-off'] },
    { id: 'fm2', title: 'Budget sync with Arjun', time: '3:00 PM', duration: '30 min', with: 'Arjun Mehta (CEO)', type: 'internal', agenda: ['Revised Q1 revenue outlook', 'Senior Developer hire budget impact', 'Q2 budget planning kickoff'] },
  ],
  dailyTimeline: [
    { time: '9:00 AM', type: 'task', title: 'Review updated cashflow projection', agent: 'finn' },
    { time: '9:30 AM', type: 'task', title: 'Approve marketing budget reallocation', agent: 'finn' },
    { time: '10:00 AM', type: 'meeting', title: 'Finance weekly review', duration: '30 min', with: 'Finance Team' },
    { time: '11:00 AM', type: 'task', title: 'Sign off on contractor payment batch', agent: 'finn' },
    { time: '1:00 PM', type: 'task', title: 'Review Senior Developer budget impact', agent: 'finn' },
    { time: '3:00 PM', type: 'meeting', title: 'Budget sync with Arjun', duration: '30 min', with: 'Arjun Mehta (CEO)' },
  ],
  inbox: [
    { id: 'fii1', sender: 'Vikram Rao (Board)', senderInitials: 'VR', time: '8:10 AM', summary: 'Requesting Q1 financial summary for board prep — needs by Wednesday EOD.', expandedSummary: 'Vikram is preparing the Q1 board materials and needs: updated revenue forecast, cashflow runway projection, headcount cost impact, and a brief variance narrative. He specifically wants the three-scenario model (low/mid/high) and asks that you flag any items needing board-level decisions. Wednesday EOD deadline.' },
    { id: 'fii2', sender: 'Priya Sharma (Marketing)', senderInitials: 'PS', time: '7:45 AM', summary: 'Budget reallocation request — $800 from Meta to LinkedIn, seeking your approval.', expandedSummary: 'Priya is requesting a reallocation within her existing Q1 marketing budget. $800 from the underperforming Meta Ad Set B (1.1% CTR) to the LinkedIn Developer Series creative (3.4% CTR). Net zero budget impact. She\'s escalating for transparency even though it\'s within her discretionary authority. Finn has already validated the numbers.' },
    { id: 'fii3', sender: 'CloudHost (Vendor)', senderInitials: 'CH', time: '7:20 AM', summary: 'Annual hosting contract renewal — 12% price increase proposed for 2025.', expandedSummary: 'CloudHost sent their renewal proposal with a 12% price increase ($2,400/month to $2,688/month). Current contract expires Feb 28. They cite infrastructure upgrades and increased usage. Finn benchmarked against 3 alternatives — AWS and Azure are comparable but migration cost is $15K. Recommends negotiating to 6-8% or locking a 2-year rate.' },
    { id: 'fii4', sender: 'David Chen (Sales)', senderInitials: 'DC', time: 'Yesterday', summary: 'Heads up on Q1 forecast revision — sharing updated pipeline data for your model.', expandedSummary: 'David sent the detailed pipeline update: TechCorp ($52K) and DataFlow ($38K) slipping to Q2, NovaTech ($28K) closing this week, and 3 late-stage SMB deals ($34K combined) that could partially close the gap. He wants to align on the narrative before your sync with Arjun at 3 PM.' },
    { id: 'fii5', sender: 'Riya Patel (People)', senderInitials: 'RP', time: 'Yesterday', summary: 'Senior Developer hire approved by Arjun — requesting budget impact confirmation.', expandedSummary: 'Riya confirmed Arjun pre-approved the Senior Developer requisition. She needs your written confirmation that the $135K-$155K salary range is within budget and won\'t trigger any financial thresholds. Also asking if the Q2 contractor bridge option needs separate budget allocation. Wants to post the job listing today.' },
  ]
};
